import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import TherapyCard from "../components/TherapyCard";
import Disclaimer from "../components/Disclaimer";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { Search, SlidersHorizontal } from "lucide-react";

const FILTERS = [
  { key: "tier",         label: "Tier",       options: [["", "Any"], ["beginner", "Beginner"], ["intermediate", "Intermediate"], ["advanced", "Advanced"]] },
  { key: "bloodwork",    label: "Bloodwork",  options: [["", "Any"], ["baseline_required", "Baseline Required"], ["recent_recommended", "Recent Labs Recommended"], ["provider_determines", "Provider Determines"]] },
  { key: "regulatory",   label: "Regulatory", options: [["", "Any"], ["fda_approved", "FDA-Approved"], ["off_label", "Off-Label"], ["compounded", "Compounded"], ["emerging", "Emerging"], ["educational", "Educational Only"]] },
  { key: "evidence",     label: "Evidence",   options: [["", "Any"], ["established", "Established"], ["moderate", "Moderate"], ["limited", "Limited"], ["preclinical", "Preclinical"]] },
  { key: "route",        label: "Route",      options: [["", "Any"], ["injectable", "Injectable"], ["oral", "Oral"], ["topical", "Topical"], ["infusion", "Infusion"]] },
];

const DEBOUNCE_MS = 250;

export default function Library() {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ tier: "", bloodwork: "", regulatory: "", evidence: "", route: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTherapies = useCallback(async () => {
    setLoading(true);
    const params = { public_only: true };
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/therapies", { params });
      setItems(data);
    } finally { setLoading(false); }
  }, [q, filters]);

  useEffect(() => {
    const t = setTimeout(fetchTherapies, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [fetchTherapies]);

  return (
    <div data-testid="library-page">
      <SEO
        title="Peptide Library — Searchable Provider-Evaluated Therapies"
        description="Search and filter provider-evaluated peptide therapies by treatment goal, tier, evidence, regulatory status, and route. Every listing is educational — a licensed provider decides suitability."
        path="/peptide-library"
        breadcrumbs={[{ name: "Peptide Library", path: "/peptide-library" }]}
      />
      <section className="vv-hero">
        <div className="vv-container py-16">
          <Breadcrumbs items={[{ name: "Peptide Library", path: "/peptide-library" }]} />
          <div className="vv-hero-eyebrow mb-2">Peptide Library</div>
          <h1 className="font-display text-4xl md:text-5xl">Provider-evaluated therapies</h1>
          <p className="mt-4 text-stone-600 max-w-2xl">Every listing is educational. A licensed provider decides suitability, dosing, and monitoring during a live consultation.</p>
        </div>
      </section>

      <section className="vv-container py-10">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex items-center gap-2 bg-white rounded-full border border-stone-200 px-4 py-2 flex-1">
            <Search size={18} className="text-stone-400" />
            <input
              data-testid="library-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by therapy name, benefit, or symptom"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <div className="text-xs text-stone-500 flex items-center gap-2"><SlidersHorizontal size={14} /> {items.length} results</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {FILTERS.map((f) => (
            <select
              key={f.key}
              data-testid={`filter-${f.key}`}
              value={filters[f.key]}
              onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
              className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm outline-none focus:border-[color:var(--vv-teal)]"
            >
              {f.options.map(([v, l]) => <option key={v} value={v}>{f.label}: {l}</option>)}
            </select>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-stone-500">Loading therapies…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-stone-500">No therapies match those filters.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((t) => <TherapyCard key={t.slug} therapy={t} />)}
          </div>
        )}

        <div className="mt-12"><Disclaimer /></div>
      </section>
    </div>
  );
}
